import type {
  AlternativePlan,
  PlanRequest,
  PlanResponse,
  ProgramTerms,
  Requirements,
  Unsatisfied,
} from "./generated";

export type {
  AcceptanceValueInput,
  AccountEnrollment,
  AccountUpdateRequest,
  AddressSearchResponse,
  AddressValue,
  AddressValueInput,
  AlternativePlan,
  AppAccountResponse,
  BillingAddressResponse,
  CardPaymentMethodValue,
  ComparisonInvoiceResponse,
  ComparisonInvoiceSimpleCompareResponse,
  CreatePaymentSessionResponse,
  DailyUsageDataResponse,
  DisallowedDatesResponse,
  DocumentsResponse,
  EligibilityRequest,
  EligibilityResponse,
  CustomerEnrollmentOptOutRequest as EnrollmentOptOutRequest,
  ESIIDSearchRequest,
  EVChargingPricingTierSchema,
  EVChargingScheduleSchema,
  IdentityRequest,
  InvoiceChargeGroupResponse,
  InvoiceChargeResponse,
  InvoiceDetailResponse,
  InvoiceResponse,
  Location,
  LocationPlan,
  MonthlyUsageDataResponse,
  OptOutReasonOption,
  PaymentMethodAddedRequest,
  PaymentMethodResponse,
  PlanHighlight,
  PlanLink,
  PlanRequest,
  PlanResponse,
  PlanValueProp,
  ProgramTerms,
  RenewalPlanResponse,
  Requirements,
  RequirementsPatch,
  SupportInfoResponse,
  Unsatisfied,
  UpdateBillingAddressRequest,
  UsagePeriodResponse,
  Utility,
} from "./generated";

export {
  AcceptedBy,
  OptOutFilter,
  OwnershipType,
  PlanType,
  ProgramType,
  ServiceEvent,
  UnsatisfiedReason,
  UtilityIdentifier,
} from "./generated";

export enum BrandMode {
  Featured = "featured",
  Subtle = "subtle",
}

// `location_uuid` is a path parameter the renewal-accept endpoint takes, merged
// into the request here for the RTK mutation arg (not part of the request body).
export type AcceptRenewalPlanRequest = {
  plan_uuid: string;
  location_uuid: string;
  service_start_date: string;
  terms_accepted: boolean;
};

export type RequirementEntry<V = unknown> = null | {
  value?: V | null;
  unsatisfied?: Unsatisfied | null;
};

export type RequirementKey = keyof Requirements;

export type Page<T> = {
  data: T[];
  has_more: boolean;
};

// Location parameters for requesting a plan, either an ESI ID or postal code + utility.
export type PlanLocation =
  | Required<Pick<PlanRequest, "esi_id">>
  | Required<Pick<PlanRequest, "postal_code" | "utility">>;

export type TermPlansRequest = PlanLocation & {
  plan_group?: string | null;
  terms: number[];
};

// One entry per requested term. `plans` is null when that term's request
// failed, so the caller can drop it from the offered options.
export type TermPlans = {
  term: number;
  plans: PlanResponse[] | null;
};

// contains all of the EFL fields + start dates
export type RateBase = {
  uuid: string;
  group_uuid: string;
  name: string;
  is_default_for_app: boolean;

  earliest_start_date: string;
  latest_start_date: string;
  term_months: number;

  avg_cents_per_kwh_500: string;
  avg_cents_per_kwh_1000: string;
  avg_cents_per_kwh_2000: string;

  energy_rate_cents_per_kwh: string;
  delivery_rate_cents_per_kwh: string;
  delivery_base_monthly_dollars: string;

  early_termination_fee_dollars: string;

  storage_monthly_credit_dollars: string;

  assumed_unlimited_kwh_usage: number;
  plan_monthly_cost_dollars: string;

  assumed_export_kwh: number;
  export_rate_cents_per_kwh: string;

  assumed_storage_kwh: string;
  storage_monthly_credit_per_kwh_dollars: string | null;

  tdu_name: string;
  tdu_shortname: string;

  renewable_percent: number;
  solar_preactivation_credit_percent: number;
  has_solar_buyback: boolean;

  device_program_terms: ProgramTerms[];
};

export enum LightIcon {
  BatteryCharging = "battery-charging",
  BatteryFull = "battery-full",
  Car = "car",
  CheckCircle = "check-circle",
  Clock = "clock",
  CloudLightning = "cloud-lightning",
  Coins = "coins",
  HeartHand = "heart-hand",
  Layers = "layers",
  Leaf = "leaf",
  PiggyBank = "piggy-bank",
  Sun = "sun",
  Tag = "tag",
  Tool = "tool",
  Truck = "truck",
}

export type LocationRequest = {
  uuid: string;
};

export type CancelServiceRequest = {
  location_uuid: string;
  final_service_date: string;
  reason?: string;
};

export type BillingInvoiceDetailsRequest = {
  invoiceNumber: string;
};

export type RateGroup = {
  uuid: string;
  name: string;
  is_default_for_app: boolean;
  term_months: number;
  default_term_month_options: number[];
  renewable_percent: number;
  early_termination_fee_dollars: string;
  has_solar_buyback: boolean;
  has_unlimited_ev_charging: boolean;
  plan_monthly_cost_dollars: string;
  storage_monthly_credit_dollars: string;
  solar_preactivation_credit_percent: number;
  storage_monthly_credit_per_kwh_dollars: string;
  assumed_storage_kwh: string;
  state: string;
  renewal_rate_group_uuid: string | null;

  device_program_terms: ProgramTerms[];
  alternative_plans?: AlternativePlan[] | null;
};

export type Vehicle = {
  vehicle_id: string;
  vin: string | null;
  year: number | null;
  brand: string | null;
  model: string | null;
  enrolled_at: string | null;
};

export type BatteryManufacturer = {
  name: string;
  display_name: string;
  supported_devices: string[];
  auth_type: string;
};

export type BatteryManufacturersResponse = BatteryManufacturer[];

export type GenerateBatteryLinkRequest = {
  redirect_link?: string;
  manufacturer?: string;
};

export type GenerateBatteryLinkResponse = {
  connect_link: string;
};

export type DayUsageRequest = LocationRequest & {
  month?: number;
  year?: number;
};

export type MonthUsageRequest = LocationRequest & {
  year?: number;
};

export type PayInvoiceRequest = {
  invoiceNumber: string;
  allow_payment_before_due_date?: boolean;
};

export type SupportCallbackRequest = {
  phone_number: string;
  context: string;
};

export type SupportCallbackAvailabilityResponse = {
  available_until: string | null;
};

// Battery device types
export type BatteryState = {
  identifier: string;
  manufacturer: string;
  model: string;
  capacity_kwh: string | null;
  is_reachable: boolean | null;
  reserve_soc: number | null;
  can_import: boolean | null;
  can_export: boolean | null;
};

export type Device = {
  uuid: string;
  device_type: "BATTERY";
  battery_details: BatteryState;
  program?: {
    uuid: string;
    is_compliant: boolean;
    compliance_checklist: BatteryChecklistItem[];
  };
};

export type BatteryChecklistItem = {
  field: string;
  label: string | null;
  expected: boolean | number | string | null;
  actual: boolean | number | string | null;
};
